import { doArraysContainSameNumbers } from "../../utils/doArraysContainSameNumbers.js";
import type { OrderProduct } from "../OrderProduct.js";
import type { ProductMacaroons } from "../ProductMacaroons.js";
import type { ProductPhoto } from "../ProductPhoto.js";
import { isSameCustomerRequirement } from "./isSameCustomerRequirement.js";

function isSamePhoto(first: ProductPhoto | undefined, second: ProductPhoto | undefined): boolean {
	if (first === undefined) {
		return second === undefined;
	}
	if (second === undefined) {
		return false;
	}

	if (first.url && second.url) {
		return first.url === second.url;
	}

	return first.file.path === second.file.path;
}

export function isSameOrderProduct(first: OrderProduct, second: OrderProduct): boolean {
	const conditions = [
		first.product.id === second.product.id,
		first.product.price.withTax === second.product.price.withTax,
		(first.options !== undefined) === (second.options !== undefined),
		first.size === second.size,
	];

	const firstOptions = first.options;
	const secondOptions = second.options;
	if (firstOptions && secondOptions) {
		const conditionMap = {
			creamColor: firstOptions.creamColor === secondOptions.creamColor,
			customerRequirement: isSameCustomerRequirement(
				firstOptions.customerRequirement,
				secondOptions.customerRequirement,
			),
			extraFruit: firstOptions.extraFruit === secondOptions.extraFruit,
			figurines:
				firstOptions.figurines.length === secondOptions.figurines.length &&
				firstOptions.figurines.every((figurine) =>
					secondOptions.figurines.some(
						(otherFigurine) =>
							figurine.description === otherFigurine.description && figurine.price === otherFigurine.price,
					),
				),
			filling: firstOptions.filling === secondOptions.filling,
			glutenFree: firstOptions.glutenFree === secondOptions.glutenFree,
			inscription: firstOptions.inscription?.text === secondOptions.inscription?.text,
			labels:
				firstOptions.labels.length === secondOptions.labels.length &&
				firstOptions.labels.every((label) => secondOptions.labels.some((otherLabel) => label.text === otherLabel.text)),
			lactoseFree: firstOptions.lactoseFree === secondOptions.lactoseFree,
			lyingPhoto: isSamePhoto(firstOptions.lyingPhoto, secondOptions.lyingPhoto),
			macaroons:
				(firstOptions.macaroons === undefined && secondOptions.macaroons === undefined) ||
				(firstOptions.macaroons !== undefined &&
					secondOptions.macaroons !== undefined &&
					Object.entries(firstOptions.macaroons).every(
						([key, value]) => secondOptions.macaroons?.[key as keyof ProductMacaroons] === value,
					)),
			cakeFilling: firstOptions.cakeFilling?.productId === secondOptions.cakeFilling?.productId,
			meringues: firstOptions.meringues === secondOptions.meringues,
			themedCupcakes: firstOptions.themedCupcakes === secondOptions.themedCupcakes,
			themedMacaroons: firstOptions.themedMacaroons === secondOptions.themedMacaroons,
			numbers: doArraysContainSameNumbers(
				firstOptions.numbers.map(({ number }) => number),
				secondOptions.numbers.map(({ number }) => number),
			),
			standingPhotos:
				firstOptions.standingPhotos.length === secondOptions.standingPhotos.length &&
				firstOptions.standingPhotos.every((photo) =>
					secondOptions.standingPhotos.some((otherPhoto) => isSamePhoto(photo, otherPhoto)),
				),
		} satisfies Record<keyof NonNullable<OrderProduct["options"]>, boolean>;

		conditions.push(...Object.values(conditionMap));
	}
	return conditions.every((condition) => condition);
}
